import { useState, useCallback } from 'react';
import {
  RadioButtonState,
  RadioButtonSize,
  RadioButtonColor,
  UseRadioButtonReturn,
} from '@/shared/types/radiobutton';

interface UseRadioButtonProps {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  size?: RadioButtonSize;
  color?: RadioButtonColor;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  name?: string;
  id?: string;
}

/**
 * Hook personalizado para manejar la lógica del RadioButton
 */
export const useRadioButton = ({
  checked,
  defaultChecked,
  disabled = false,
  onChange,
  value = '',
  name,
  id,
}: UseRadioButtonProps): UseRadioButtonReturn => {
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Generar ID único si no se proporciona
  const radioId = id || `radio-${Math.random().toString(36).substr(2, 9)}`;

  // Determinar si es controlado o no controlado
  const isControlled = checked !== undefined;
  const currentChecked = isControlled
    ? checked
    : defaultChecked !== undefined
      ? defaultChecked
      : false;

  // Determinar el estado actual del radio button
  const getState = useCallback((): RadioButtonState => {
    if (disabled) {
      return currentChecked ? 'disabledChecked' : 'disabledUnchecked';
    }

    if (isActive) {
      return currentChecked ? 'checkedActive' : 'uncheckedActive';
    }

    if (isHovered) {
      return currentChecked ? 'checkedHover' : 'uncheckedHover';
    }

    return currentChecked ? 'checked' : 'unchecked';
  }, [disabled, currentChecked, isActive, isHovered]);

  // Handlers para eventos del mouse
  const handleMouseDown = useCallback(() => {
    if (!disabled) {
      setIsActive(true);
    }
  }, [disabled]);

  const handleMouseUp = useCallback(() => {
    setIsActive(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!disabled) {
      setIsHovered(true);
    }
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setIsActive(false);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();

      // Simular el evento del input radio
      if (onChange) {
        const syntheticEvent = {
          target: {
            value,
            checked: !currentChecked,
            type: 'radio' as const,
            name,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onChange(syntheticEvent);
      }
    },
    [disabled, onChange, value, currentChecked, name]
  );

  return {
    state: getState(),
    radioId,
    isActive,
    isHovered,
    handlers: {
      handleMouseDown,
      handleMouseUp,
      handleMouseEnter,
      handleMouseLeave,
      handleClick,
    },
  };
};
